import { useMemo, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import apiClient from "../../../api/apiClient";
import { ShowData } from "../Show";
import { ColDef } from "@ag-grid-community/core";
import { shortDateString } from "../../../utils/dateFormatters";
import { useTeamService } from "../../../api/teamService";
import {
  CommissionRow,
  MgrCommissionDataRowAdapter,
} from "./MgrCommissionDataRow";

const MgrCommissionAnalytics = () => {
  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const [rowData, setRowData] = useState<CommissionRow[]>([]);
  const { teamsForMember } = useTeamService();

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      minWidth: 150,
      suppressAutoSize: false,
      filter: true,
      resizable: true,
      enableRowGroup: true,
      sortable: true,
      defaultAggFunc: "sum",
    };
  }, []);

  const [columnDefs] = useState<ColDef[]>([
    {
      field: "name",
      headerName: "Name",
      minWidth: 200,
      rowGroup: true,
      hide: true,
    },
    {
      field: "team",
      headerName: "Artist",
      minWidth: 180,
    },
    {
      field: "performanceDate",
      headerName: "Date",
      valueFormatter: (p: any) => {
        if (p.node && p.node.group) {
          // Return the raw value or a different format for group rows
          return "";
        }
        return shortDateString(p.value);
      },
      minWidth: 120,
    },
    {
      field: "venue",
      headerName: "Venue",
      minWidth: 180,
    },
    {
      field: "walkout",
      headerName: "Show Walkout",
      valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
      minWidth: 150,
      aggFunc: "sum",
    },
    {
      field: "commissionPercent",
      headerName: "Commission %",
      valueFormatter: (p: any) => Math.round(p.value * 100) / 100 + "%",
      minWidth: 150,
      aggFunc: "avg",
    },
    {
      field: "commissionAmount",
      headerName: "Commission $",
      valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
      minWidth: 150,
      aggFunc: "sum",
    },
    {
      field: "commissionBuyout",
      headerName: "Commission Buyout",
      valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
      minWidth: 150,
      aggFunc: "sum",
    },
    {
      field: "flatFee",
      headerName: "Flat Fee",
      valueFormatter: (p: any) => "$" + Math.floor(p.value).toLocaleString(),
      minWidth: 150,
      aggFunc: "sum",
    },
    {
      field: "city",
      headerName: "City",
      minWidth: 120,
    },
    {
      field: "state",
      headerName: "State",
      minWidth: 100,
    },
  ]);

  const getData = async () => {
    try {
      const shows: ShowData[] = await apiClient
        .get("/multi-team/show", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        })
        .then((resp) => resp.data);

      setRowData(
        new MgrCommissionDataRowAdapter().toContracts(shows, teamsForMember)
      );
    } catch (error) {
      setRowData([]);
      console.error("Error fetching commission data:", error);
    }
  };

  useEffect(() => {
    getData();
  }, [teamsForMember]);

  return (
    <div style={containerStyle}>
      <div style={gridStyle} className={"ag-theme-quartz"}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          sideBar={{ toolPanels: ["columns", "filters"] }}
          enableCharts={true}
          rowGroupPanelShow={"always"}
          groupDisplayType="multipleColumns"
          cellSelection={true}
          suppressAggFuncInHeader={true}
          autoGroupColumnDef={{
            minWidth: 200,
          }}
        />
      </div>
    </div>
  );
};

export default MgrCommissionAnalytics;
